import type { fabric } from 'fabric';
import type { EditorPlugin, FabricEditor } from 'modules/fabric-editor';
import { FabricPluginName } from 'modules/fabric-editor';
import {
  createLayoutGroup,
  findLayoutGroup,
  generateBlockKey,
  getLayoutBlockData,
} from './layout-blocks-factory';
import { DEFAULT_LAYOUT_PRESETS } from './presets';
import type {
  LayoutBlockData,
  LayoutBlockFitMode,
  LayoutBlockRegionData,
  LayoutBlocksFacade,
  LayoutBlocksPluginOptions,
  LayoutPreset,
} from './types';

declare module 'modules/fabric-editor/domain/core/fabric-editor' {
  interface FabricEditor {
    layoutBlocks?: LayoutBlocksFacade;
  }
}

const DEFAULT_OPACITY = 1;
const DEFAULT_FIT_MODE: LayoutBlockFitMode = 'cover';

export function layoutBlocksPlugin(options: LayoutBlocksPluginOptions = {}): EditorPlugin {
  const defaults = {
    opacity: clampOpacity(options.defaultOpacity ?? DEFAULT_OPACITY),
    fitMode: options.defaultFitMode ?? DEFAULT_FIT_MODE,
  };

  return {
    name: FabricPluginName.LAYOUT_BLOCKS,
    install(editor: FabricEditor) {
      const canvas = editor.canvas;
      const presets = mergePresets(options.presets);
      const presetById = new Map(presets.map((p) => [p.id, p]));

      editor.registerObjectType<LayoutBlockData>({
        kind: 'layout-block',
        create: (data) => {
          const preset = presetById.get(data.presetId);
          if (!preset) throw new Error(`Unknown layout preset: ${data.presetId}`);
          return createLayoutGroup(data, editor.resolution, preset);
        },
      });

      const enforceZOrder = (group: fabric.Group) => {
        canvas.sendToBack(group);
        // Why: grid plugin pins itself to the back on rebuild — calling
        // rebuild() here drops grid below the layout, giving the spec stack:
        // grid (0) → layout (1) → user objects.
        editor.grid?.rebuild();
      };

      const removeExisting = (): void => {
        const existing = findLayoutGroup(canvas);
        if (existing) canvas.remove(existing);
      };

      const materialize = (data: LayoutBlockData): fabric.Group => {
        const preset = presetById.get(data.presetId);
        if (!preset) throw new Error(`Unknown layout preset: ${data.presetId}`);
        removeExisting();
        const group = createLayoutGroup(data, editor.resolution, preset);
        canvas.add(group);
        enforceZOrder(group);
        canvas.requestRenderAll();
        editor.emit('object:modified', { target: group });
        return group;
      };

      const updateData = (mutate: (draft: LayoutBlockData) => LayoutBlockData | void): void => {
        const existing = findLayoutGroup(canvas);
        const current = existing ? (getLayoutBlockData(existing) as LayoutBlockData) : undefined;
        if (!existing || !current) return;
        const next = { ...current, blocks: current.blocks.map((b) => ({ ...b })) };
        const mutated = mutate(next) ?? next;
        materialize(mutated);
      };

      const rebuild = (): void => {
        const existing = findLayoutGroup(canvas);
        const data = existing ? getLayoutBlockData(existing) : undefined;
        if (!existing || !data) return;
        materialize(data);
      };

      const offCanvasLoaded = editor.on('canvas:loaded', () => rebuild());

      const facade: LayoutBlocksFacade & { __off?: () => void } = {
        apply(presetId) {
          const preset = presetById.get(presetId);
          if (!preset) throw new Error(`Unknown layout preset: ${presetId}`);
          const existing = findLayoutGroup(canvas);
          const previous = existing ? getLayoutBlockData(existing) : undefined;
          const blocks: LayoutBlockRegionData[] = preset.regions.map((_, i) => {
            const prev = previous?.blocks[i];
            return {
              placeholderKey: prev?.placeholderKey ?? generateBlockKey(presetId, i),
              placeholderLabel: prev?.placeholderLabel ?? `Block ${i + 1}`,
              fitMode: prev?.fitMode ?? defaults.fitMode,
            };
          });
          const data: LayoutBlockData = {
            kind: 'layout-block',
            isSave: true,
            presetId,
            opacity: previous?.opacity ?? defaults.opacity,
            blocks,
          };
          return materialize(data);
        },
        remove() {
          removeExisting();
          canvas.requestRenderAll();
        },
        get() {
          return findLayoutGroup(canvas);
        },
        getData() {
          const group = findLayoutGroup(canvas);
          return group ? getLayoutBlockData(group) : undefined;
        },
        setOpacity(value) {
          updateData((draft) => {
            draft.opacity = clampOpacity(value);
          });
        },
        setBlockLabel(index, label) {
          updateData((draft) => {
            if (!draft.blocks[index]) return;
            draft.blocks[index].placeholderLabel = label;
          });
        },
        setBlockKey(index, key) {
          updateData((draft) => {
            if (!draft.blocks[index]) return;
            draft.blocks[index].placeholderKey = key;
          });
        },
        setBlockFitMode(index, mode) {
          updateData((draft) => {
            if (!draft.blocks[index]) return;
            draft.blocks[index].fitMode = mode;
          });
        },
        listPresets() {
          return presets.slice();
        },
        rebuild,
      };

      facade.__off = () => {
        offCanvasLoaded();
      };

      editor.layoutBlocks = facade;
    },
    dispose(editor: FabricEditor) {
      const facade = editor.layoutBlocks as (LayoutBlocksFacade & { __off?: () => void }) | undefined;
      facade?.__off?.();
      const existing = findLayoutGroup(editor.canvas);
      if (existing) editor.canvas.remove(existing);
      editor.layoutBlocks = undefined;
    },
  };
}

function clampOpacity(value: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.max(0, Math.min(1, value));
}

function mergePresets(extra: LayoutPreset[] | undefined): LayoutPreset[] {
  if (!extra || extra.length === 0) return DEFAULT_LAYOUT_PRESETS.slice();
  const map = new Map<string, LayoutPreset>();
  for (const p of DEFAULT_LAYOUT_PRESETS) map.set(p.id, p);
  for (const p of extra) map.set(p.id, p);
  return Array.from(map.values());
}
